import { Language, localeFor, MessageKey } from '../i18n';
import type {
    DailyCard,
    DailyReading,
    SpreadNote,
    TarotContent,
} from '../i18n/types';

import { cardById } from './deck';
import { DrawnCard, Reading } from './draw';
import { Position, PositionKey, SPREADS, Theme } from './spreads';

export type Verdict = 'yes' | 'no' | 'maybe';

export interface CardInterpretation {
    name: string;
    keywords: string;
    meaning: string;
    advice: string;
    verdict: Verdict;
    /** The hand-written text for this card in this position of a love, work or path spread. */
    note?: SpreadNote;
    /** The hand-written card of the day for this card, when the language has one. It replaces the generic texts above. */
    daily?: DailyReading;
    /** The hand-written long reading of the card through the spread's lens (love, work or general), when the language has one. */
    lens?: DailyReading;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Fills ", {name}" with the person's name, or drops the vocative when there is no name. */
export const withName = (text: string, name?: string): string =>
    name
        ? text.replace(/\{name\}/g, name)
        : text.replace(/,\s*\{name\}/g, '').replace(/\{name\}/g, '');

/** The word-by-word fallback for `{name}` when the love reading has no name (see SpreadsContent.other). */
const fillOther = (
    text: string,
    other: Record<string, string>,
    name?: string
): string => {
    if (name) return text.replace(/\{name\}/g, name);
    const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    return (
        text
            // "de {name}" -> "da outra pessoa": the preceding word is absorbed by the contraction.
            .replace(/(\p{L}+) \{name\}/gu, (all, word: string) => {
                const phrase = other[word.toLowerCase()];
                if (phrase === undefined) return `${word} ${other['']}`;
                return word[0] === word[0].toUpperCase()
                    ? upper(phrase)
                    : phrase;
            })
            .replace(/(^|[.!?…]\s+)\{name\}/g, (_all, lead: string) => {
                return `${lead}${upper(other[''])}`;
            })
            .replace(/\{name\}/g, other[''])
    );
};

/** Fills a spread text: `{name}` is the other person, `{me}` the reader (as a dropped-if-unknown vocative). */
export const fillSpread = (
    text: string,
    language: Language,
    name?: string,
    me?: string
): string => {
    const other = localeFor(language).spreads.other;
    const withMe = me
        ? text.replace(/\{me\}/g, me)
        : text.replace(/,\s*\{me\}/g, '').replace(/\{me\}/g, '');
    return fillOther(withMe, other, name);
};

const dailyReadingOf = (
    cardId: string,
    reversed: boolean,
    language: Language,
    name?: string
): DailyReading | undefined => {
    const entry = localeFor(language).daily.cards[cardId];
    return entry && readingOf(entry, reversed, name);
};

/** The long reading of a card in a lens (love, work, general), without a name: those texts never address anyone. */
const lensReadingOf = (
    cardId: string,
    reversed: boolean,
    theme: Theme,
    language: Language
): DailyReading | undefined => {
    const entry = localeFor(language).lenses[theme]?.[cardId];
    return entry && readingOf(entry, reversed);
};

const readingOf = (
    entry: DailyCard,
    reversed: boolean,
    name?: string
): DailyReading => {
    const r = reversed ? entry.r : entry.u;
    return {
        title: r.title,
        body: r.body.map((p) => withName(p, name)),
        advice: withName(r.advice, name),
    };
};

/** Upright answer of each major arcana (index = card number) to a yes/no question. */
const MAJOR_VERDICT: Verdict[] = [
    'yes', // Fool
    'yes', // Magician
    'maybe', // High Priestess
    'yes', // Empress
    'yes', // Emperor
    'maybe', // Hierophant
    'yes', // Lovers
    'yes', // Chariot
    'yes', // Strength
    'maybe', // Hermit
    'yes', // Wheel
    'maybe', // Justice
    'maybe', // Hanged Man
    'no', // Death
    'yes', // Temperance
    'no', // Devil
    'no', // Tower
    'yes', // Star
    'maybe', // Moon
    'yes', // Sun
    'maybe', // Judgement
    'yes', // World
];

/** Upright answer by minor rank (index = rank - 1, ace to king). */
const MINOR_VERDICT: Verdict[] = [
    'yes',
    'maybe',
    'yes',
    'maybe',
    'no',
    'yes',
    'maybe',
    'yes',
    'yes',
    'maybe',
    'yes',
    'yes',
    'yes',
    'yes',
];

const flip = (v: Verdict): Verdict =>
    v === 'yes' ? 'no' : v === 'no' ? 'yes' : 'maybe';

const isSharp = (suit: string | undefined, rank: number): boolean =>
    (suit === 'swords' && [3, 5, 9, 10].includes(rank)) ||
    ((suit === 'cups' || suit === 'pentacles') && rank === 5);

/** The answer of a card to a yes/no question; reversed flips it. Sharp cards lean to "no". */
export const verdictOf = (cardId: string, reversed: boolean): Verdict => {
    const card = cardById(cardId);
    if (!card) return 'maybe';
    let base: Verdict;
    if (card.arcana === 'major') {
        base = MAJOR_VERDICT[card.rank];
    } else {
        base = MINOR_VERDICT[card.rank - 1];
        if (isSharp(card.suit, card.rank)) base = 'no';
        if (card.suit === 'wands' && card.rank === 10) base = 'maybe';
    }
    return reversed ? flip(base) : base;
};

export const interpretCard = (
    cardId: string,
    reversed: boolean,
    theme: Theme,
    language: Language,
    /** Who the card of the day is addressed to; only used by the daily reading. */
    name?: string
): CardInterpretation => {
    const base = interpretGeneric(cardId, reversed, theme, language);
    const daily = dailyReadingOf(cardId, reversed, language, name);
    const lens = lensReadingOf(cardId, reversed, theme, language);
    return { ...base, ...(daily && { daily }), ...(lens && { lens }) };
};

const interpretGeneric = (
    cardId: string,
    reversed: boolean,
    theme: Theme,
    language: Language
): CardInterpretation => {
    const c: TarotContent = localeFor(language).cards;
    const card = cardById(cardId);
    if (!card) throw new Error(`Unknown card ${cardId}`);
    const verdict = verdictOf(cardId, reversed);

    if (card.arcana === 'major') {
        const m = c.major[card.rank];
        const lens = theme === 'love' ? 'l' : theme === 'work' ? 'w' : '';
        const key = `${lens}${reversed ? 'r' : 'u'}` as keyof typeof m;
        return {
            name: m.n,
            keywords: m.k,
            meaning: m[key],
            advice: reversed ? m.ar : m.au,
            verdict,
        };
    }

    const rank = c.ranks[card.rank - 1];
    const suit = c.suits[card.suit!];
    const state = reversed ? rank.rev : rank.up;
    return {
        name: c.minorName
            .replace('{rank}', rank.name)
            .replace('{suit}', suit.name),
        keywords: `${rank.k} · ${suit.k}`,
        meaning: `${cap(state)} ${suit.in[theme]}. ${reversed ? suit.tone.rev : suit.tone.up}`,
        advice: reversed ? rank.ar : rank.au,
        verdict,
    };
};

/** A card's long reading as it falls in one position of a spread: a lead for the position, and the paragraphs that suit it. */
export interface PositionReading {
    lead: string;
    title: string;
    paragraphs: string[];
    /** The line to carry, only where the position gives advice. */
    advice?: string;
}

export interface PositionedCard extends CardInterpretation {
    position: PositionKey;
    shows: 'meaning' | 'advice' | 'both';
    drawn: DrawnCard;
    /** The lens reading composed for this position, when the language has one (never for the card of the day). */
    positioned?: PositionReading;
}

/**
 * Which paragraphs of a lens reading (meaning, how it unfolds, what to reflect on) a position shows. The
 * positions that give advice keep the closing paragraph; a position not listed shows all three.
 */
const PARAGRAPHS: Partial<Record<PositionKey, number[]>> = {
    past: [0, 1],
    obstacle: [0, 1],
    future: [0, 2],
    guidance: [2],
    advice: [2],
};

/** The lens reading of a card composed for its position: the lead of the position and orientation, then the paragraphs that suit it. */
export const positionReadingOf = (
    long: DailyReading,
    position: Position,
    reversed: boolean,
    language: Language,
    name?: string,
    me?: string
): PositionReading => {
    const ui = localeFor(language).ui;
    const lead = ui[`lead.${position.key}${reversed ? '.rev' : ''}`];
    const keep = PARAGRAPHS[position.key];
    return {
        lead: fillSpread(lead, language, name, me),
        title: long.title,
        paragraphs: keep ? keep.map((i) => long.body[i]) : long.body,
        ...(position.shows !== 'meaning' && { advice: long.advice }),
    };
};

export const interpretReading = (
    reading: Reading,
    language: Language
): PositionedCard[] => {
    const spread = SPREADS[reading.spread];
    const spreads = localeFor(language).spreads.cards;
    return reading.cards.map((drawn, i) => {
        const position = spread.positions[i];
        const raw =
            reading.spread === 'daily' || reading.spread === 'yesno'
                ? undefined
                : spreads[drawn.id]?.[drawn.reversed ? 'r' : 'u']?.[
                      position.key
                  ];
        const note = raw && {
            text: fillSpread(raw.text, language, reading.name, reading.me),
            ask: fillSpread(raw.ask, language, reading.name, reading.me),
        };
        const card = interpretCard(
            drawn.id,
            drawn.reversed,
            spread.theme,
            language,
            reading.spread === 'daily' ? reading.name : undefined
        );
        const positioned =
            reading.spread !== 'daily' && card.lens
                ? positionReadingOf(
                      card.lens,
                      position,
                      drawn.reversed,
                      language,
                      reading.name,
                      reading.me
                  )
                : undefined;
        return {
            ...card,
            ...(positioned ? { positioned } : {}),
            ...(note ? { note } : {}),
            position: position.key,
            shows: position.shows,
            drawn,
        };
    });
};

export type OverallKey =
    'overall.fated' | 'overall.open' | 'overall.mixed' | 'overall.blocked';

/** One closing line about the whole spread, from how many cards are reversed and how many are major arcana. */
export const overallOf = (cards: DrawnCard[]): OverallKey | null => {
    if (cards.length < 3) return null;
    const reversed = cards.filter((c) => c.reversed).length;
    const majors = cards.filter((c) => c.id.startsWith('major')).length;
    if (majors >= Math.ceil(cards.length / 2) && reversed <= 1)
        return 'overall.fated';
    if (reversed === 0) return 'overall.open';
    if (reversed >= Math.ceil(cards.length / 2)) return 'overall.blocked';
    return 'overall.mixed';
};

export const PROMPT_COUNT = 6;

/** The reflection question to leave the person with, picked from a fixed pool by the reading's seed. */
export const promptKeyOf = (reading: Reading): MessageKey => {
    const theme = SPREADS[reading.spread].theme;
    const n = Math.abs(reading.seed) % PROMPT_COUNT;
    return `prompt.${theme}.${n}` as MessageKey;
};

/** The names of the suits, as used in the summary keys. */
const SUITS = ['wands', 'cups', 'swords', 'pentacles'] as const;

/**
 * The paragraphs that close a love, work or path spread: the cards read as one story, the suit that
 * dominates, and the reversed cards to look at first. The yes/no spread closes with one line on its answer;
 * the card of the day has none.
 */
export const summaryOf = (
    reading: Reading,
    cards: PositionedCard[],
    language: Language
): string[] => {
    const spread = reading.spread;
    if (spread === 'yesno')
        return [
            fillSpread(
                localeFor(language).ui[`summary.yesno.${cards[0].verdict}`],
                language
            ).replace(/\{c0\}/g, cards[0].name),
        ];
    if (spread !== 'love' && spread !== 'work' && spread !== 'path') return [];
    const ui = localeFor(language).ui;
    const fill = (text: string) =>
        fillSpread(
            text.replace(/\{c(\d)\}/g, (_m, n) => cards[+n]?.name ?? ''),
            language,
            reading.name,
            reading.me
        );
    const out: string[] = [fill(ui[`summary.thread.${spread}`])];

    const counts = SUITS.map((suit) => ({
        suit,
        n: reading.cards.filter((c) => c.id.startsWith(`${suit}-`)).length,
    })).sort((a, b) => b.n - a.n);
    if (counts[0].n >= 2 && counts[0].n > counts[1].n)
        out.push(fill(ui[`summary.suit.${counts[0].suit}`]));

    const reversed = cards.filter((c) => c.drawn.reversed);
    if (reversed.length > 0 && reversed.length < cards.length) {
        const list = reversed.map((c) => `“${c.name}”`).join(', ');
        out.push(
            fill(
                ui[
                    reversed.length === 1
                        ? 'summary.reversed.one'
                        : 'summary.reversed.many'
                ].replace('{list}', list)
            )
        );
    }
    return out;
};

/** The reflection questions to close with: two from the pool, picked by the reading's seed. */
export const closingPromptKeys = (reading: Reading): MessageKey[] => {
    const theme = SPREADS[reading.spread].theme;
    const n = Math.abs(reading.seed) % PROMPT_COUNT;
    const m = (n + 3) % PROMPT_COUNT;
    return [n, m].map((i) => `prompt.${theme}.${i}` as MessageKey);
};
